import {APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideRouter, RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {environment} from '@env-link/environment';
import {provideStore, Store} from "@ngrx/store";
import {provideEffects} from "@ngrx/effects";
import {provideStoreDevtools} from "@ngrx/store-devtools";
import {IonicStorageModule} from "@ionic/storage-angular";
import {Drivers} from '@ionic/storage';
import {appStore} from "@core/store/core-app.reducer";
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from "@angular/common/http";
import {linkStore} from "@link/store/link.reducer";
import {LinkApiEffects} from "@link/store/link-api.effect";
import {DefaultDataServiceConfig, EntityDataModule, EntityMetadataMap, provideEntityData} from "@ngrx/data";
import {provideTranslateService, TranslateLoader} from '@ngx-translate/core';
import {SharedTimeoutInterceptor} from "@shared/interceptors/shared-timeout.interceptor";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {attachmentsStore} from "@shared/components/attachments/store/attachments.reducers";
import {AttachmentsApiEffects} from "@shared/components/attachments/store/attachments-api.effects";
import routes from "@link/routes";
import {LinkAppComponent} from "@link/link-app.component";
import CordovaSQLiteDriver from "localforage-cordovasqlitedriver";
import {linkIssuesStore} from "@link/pages/issues/store/link-issues.reducers";
import {defineCustomElements} from "@ionic/pwa-elements/loader";
import {LinkIssuesApiEffects} from "@link/pages/issues/store/link-issues-api.effects";
import {CoreCustomTranslateHttpLoaderService} from "@core/services/core-custom-translate-http-loader.service";
import {sharedChatStore} from "@shared/components/chat/store/shared-chat.reducers";
import {SharedChatApiEffects} from "@shared/components/chat/store/shared-chat.api-effects";
import {sharedPermissionsStore} from "@shared/stores/permissions/store/shared-permissions.reducer";
import {SharedPermissionsEffects} from "@shared/stores/permissions/store/shared-permissions.effects";
import {sharedLoginStore} from "@shared/components/login/store/shared-login.reducer";
import {SharedLoginEffects} from "@shared/components/login/store/shared-login.effects";
import {SharedLoginActions} from "@shared/components/login/store/shared-login.actions-type";


if (environment.production) {
  enableProdMode();
}

defineCustomElements(window);

const entityMetadataMap: EntityMetadataMap = {
  Chat: {
    selectId: chat => chat.chatByFormDefinitionId
  }
};

const defaultDataServiceConfig: DefaultDataServiceConfig = {
  root: environment.apiUrl.one
}

export function createTranslateLoader(http: HttpClient): CoreCustomTranslateHttpLoaderService {
  return new CoreCustomTranslateHttpLoaderService(http);
}

export function setBrandInitializer(store: Store): () => void {
  return () => store.dispatch(SharedLoginActions.setBrand({ brand: environment.brand }));
}

bootstrapApplication(LinkAppComponent, {
  providers: [
    provideZoneChangeDetection(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: DefaultDataServiceConfig, useValue: defaultDataServiceConfig },
    { provide: HTTP_INTERCEPTORS, useClass: SharedTimeoutInterceptor, multi: true },
    provideStore({
      app: appStore,
      link: linkStore,
      issues: linkIssuesStore,
      attachments: attachmentsStore,
      sharedLogin: sharedLoginStore,
      sharedChat: sharedChatStore,
      sharedPermissions: sharedPermissionsStore,
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: setBrandInitializer,
      deps: [Store],
      multi: true
    },
    provideEffects([
      LinkApiEffects,
      LinkIssuesApiEffects,
      AttachmentsApiEffects,
      SharedLoginEffects,
      SharedChatApiEffects,
      SharedPermissionsEffects
    ]),
    provideStoreDevtools({
      name: 'semco ONE'
    }),
    provideRouter(routes),
    provideEntityData({ entityMetadata: entityMetadataMap }),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    importProvidersFrom(
      IonicModule.forRoot({
        spinner: 'dots',
        swipeBackEnabled: false,
      }),
      IonicStorageModule.forRoot({
        name: 'one',
        driverOrder: [
          CordovaSQLiteDriver._driver,
          Drivers.IndexedDB,
          Drivers.LocalStorage
        ]
      }),
      HttpClientModule,
      BrowserAnimationsModule,
      EntityDataModule.forRoot({})
    ),
  ]
});
